import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

interface BlockProps {
  left: number;
}

const blockWidth = '64px';

const Block = styled.div<BlockProps>`
  position: absolute;
  left: ${(p) =>
    `min(max(calc(${p.left}% - (${blockWidth} / 2)),
    ${tokens.spacer.x0}),
    100% - (${blockWidth} + ${tokens.spacer.x0}))`};
  background: ${tokens.BRAND_COLORS.neutral_800};
  color: ${tokens.color.white};
  padding: ${tokens.spacer.x2} 0;
  width: ${blockWidth};
  text-align: center;
  border-radius: ${tokens.radius.l};
  ${tokens.text('header4')};
`;

interface LineProps {
  left: number;
}

const Line = styled.div<LineProps>`
  position: absolute;
  width: 1px;
  background: ${tokens.color.white};
  left: ${(p) => `${p.left}%`};
  height: 100%;
`;

const Gradient = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 140px;
  border-radius: ${tokens.radius.l};
  background: linear-gradient(
    to right,
    ${tokens.color.crimson_light} 25%,
    ${tokens.color.tangerine_light} 50%,
    ${tokens.BRAND_COLORS.mint_500} 75%
  );
`;

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
  ${tokens.text('subtitle2')};
`;

const Zero = styled.span`
  margin-left: -3px;
`;

export interface NPSGradientChartProps {
  score?: number;
}

export function NPSGradientChart({ score }: NPSGradientChartProps): JSX.Element | null {
  const { t, i18n } = useTranslation('feedback', { keyPrefix: 'results.nps-view-mode' });
  if (score === undefined) return null;

  const boundedScore = Math.max(-100, Math.min(100, score));
  const left = (boundedScore + 100) / 2;
  const numberFormatter = new Intl.NumberFormat(i18n.language);
  const roundedScore = numberFormatter.format(Math.round(boundedScore));

  return (
    <Container role="img" aria-label={t('nps-score', { score: roundedScore })}>
      <Gradient>
        <Line left={left} />
        <Block title={t('score')} left={left}>
          {roundedScore}
        </Block>
      </Gradient>
      <Labels>
        <span>{numberFormatter.format(-100)}</span>
        <Zero>{numberFormatter.format(0)}</Zero>
        <span>{numberFormatter.format(100)}</span>
      </Labels>
    </Container>
  );
}
